/**
 * decisionAttribution.js
 * ----------------------
 * Pure helpers describing WHO recorded a triage decision, and with whom, for
 * display wherever a decided referral is shown ("Decided by {name}" /
 * "Decided by {name} · with {name}, {name}").
 *
 * Both review shapes are supported:
 * - New structured ADHD reviews carry `cliniciansInvolved`
 *   ([{ clinicianId, clinicianName, involved }]) alongside `decidedBy`. The
 *   attribution is the decider plus every OTHER clinician marked involved
 *   (the decider always appears in the roster and is excluded by id so they
 *   are not listed twice).
 * - Legacy / autism reviews carry only `decidedBy` — the attribution is the
 *   decider of the decisive review. For autism dual-review discharge records
 *   (two rejections) the FINAL review's decider is attributed, and the FIRST
 *   rejecting reviewer is listed among the involved names — the discharge is
 *   the product of both opinions, so "Triaged by" must name both.
 *
 * Display-only fallback: a clinician whose stored name is empty renders as
 * "Unnamed clinician" — the underlying record is never altered.
 */

export const UNNAMED_CLINICIAN = 'Unnamed clinician';

/** Trimmed clinician name, falling back to "Unnamed clinician" for display. */
const displayName = (name) => String(name || '').trim() || UNNAMED_CLINICIAN;

/**
 * The attribution for a decided referral.
 *
 * The decided review is the accepted review when one exists (an acceptance
 * ends triage immediately); otherwise the final review carries the decision
 * (the 2nd rejection that discharges an autism referral, or a single ADHD
 * MDT rejection).
 *
 * @param {Array} reviews — the triage reviews for the decided pathway, in
 *   submission order (e.g. from `pathwayReviews(patient, pathway)`).
 * @returns {{ deciderName: string, involvedNames: string[] } | null}
 *   null when there is no decided review to attribute.
 */
export function getDecisionAttribution(reviews) {
  if (!Array.isArray(reviews) || reviews.length === 0) return null;
  const decided = reviews.find((r) => r?.accepted) || reviews[reviews.length - 1];
  if (!decided) return null;

  const deciderId = decided.decidedBy?.clinicianId;
  const involved = Array.isArray(decided.cliniciansInvolved)
    ? decided.cliniciansInvolved
    : [];

  const involvedNames = involved
    .filter((c) => c && c.involved === true && c.clinicianId !== deciderId)
    .map((c) => displayName(c.clinicianName));

  // A dual-review (autism) DISCHARGE is the product of both rejections, each
  // held as its own review entry — so the earlier reviews' deciders are also
  // "involved"; without them the first rejecting reviewer is silently dropped
  // from "Triaged by". An acceptance stays attributed to the acceptor alone.
  if (decided.accepted !== true) {
    const seen = new Set(involvedNames);
    for (const review of reviews) {
      if (review === decided) continue;
      const id = review?.decidedBy?.clinicianId;
      if (id && id === deciderId) continue;
      const name = displayName(review?.decidedBy?.clinicianName);
      if (!review?.decidedBy || seen.has(name)) continue;
      seen.add(name);
      involvedNames.push(name);
    }
  }

  return {
    deciderName: displayName(decided.decidedBy?.clinicianName),
    involvedNames,
  };
}

/**
 * Formats an attribution as its display label:
 * "Decided by {name}" or "Decided by {name} · with {name}, {name}".
 * Returns '' for a null attribution so callers can render nothing.
 */
export function formatDecisionAttribution(attribution) {
  if (!attribution) return '';
  const { deciderName, involvedNames } = attribution;
  const base = `Decided by ${deciderName}`;
  return involvedNames && involvedNames.length
    ? `${base} · with ${involvedNames.join(', ')}`
    : base;
}

/**
 * The plain list of clinicians involved in the triage, decider first, e.g.
 * "Eve Clinician, Sai Ulluri". Used for the Waiting List "Triaged by" column,
 * which names who triaged rather than framing it as a single decider. Returns
 * '' for a null attribution.
 */
export function formatCliniciansInvolved(attribution) {
  if (!attribution) return '';
  const { deciderName, involvedNames } = attribution;
  return [deciderName, ...(involvedNames || [])].filter(Boolean).join(', ');
}

/** Shown in place of the first reviewer's name while a second review is open. */
export const WITHHELD_REVIEWER_NAME = 'Another clinician';

/**
 * Whether this viewer may see the first reviewer's name while a second
 * review is still open (LSC-039/LSC-040, TDI-403).
 *
 * The second opinion has to be formed on the content of the first review, not
 * on who wrote it, so the name is withheld from whoever is forming it. Three
 * people may still see it: the first reviewer (it is their own name), RMS
 * Support (operational oversight, never a clinical opinion), and an Org.
 * Admin who is not the one reviewing.
 *
 * The case this exists for: an Org. Admin assigned as the second reviewer
 * used to see the name anyway, because the check was "can force unassign",
 * which is an administrative power rather than a statement about whether they
 * are judging the referral.
 *
 * @param {{ isFirstReviewer?: boolean, userType?: string,
 *           isAssignedSecondReviewer?: boolean }} viewer
 * @returns {boolean}
 */
export function canSeeFirstReviewerName({
  isFirstReviewer = false,
  userType = '',
  isAssignedSecondReviewer = false,
} = {}) {
  if (isFirstReviewer) return true;
  if (userType === 'RMS Support') return true;
  if (userType === 'RMS Org. Admin') return !isAssignedSecondReviewer;
  return false;
}
